// Block drops (PLR-2 drop gating; §3.3 loot). PURE (ENG-1).
//
// Resolves what a broken block yields: its own item (`dropSelf`) or a loot
// table's item entries. Tool-tier gating (wrong tier → nothing) is applied here
// so both the instant-break path (M4.2) and the timed survival break (M4.3) share
// one rule. Weighted rolls / loot functions stay minimal until the full loot
// engine (M6); for M4 every listed entry drops one (or its `set_count` min).
import type { Registries } from '../registry/load';
import type { LootTable } from '../registry/schemas';
import { TOOL_TIERS } from '../registry/schemas';
import { type ItemStack, makeStack } from './inventory';
import { type Ench, enchantLevelOf, hasSilkTouch, fortuneMultiplier } from './enchant_effects';

/** Held-tool descriptor for drop gating (null = bare hand / non-tool). */
export interface HeldTool {
  toolClass?: string;
  toolTier?: string;
  /** Enchantments on the held tool (efficiency/fortune/silk-touch/unbreaking). */
  enchants?: readonly Ench[];
}

/** A block whose harvested drops differ from itself (ore/glass/leaves/sculk) —
 *  the ones Silk Touch and Fortune act on. */
function harvestsSelf(name: string, lootTable: string | undefined): boolean {
  return name.endsWith('_ore') || (lootTable?.startsWith('ores/') ?? false);
}

const TIER_RANK: ReadonlyMap<string, number> = new Map(TOOL_TIERS.map((t, i) => [t, i]));

/** Whether `tool` can harvest a block requiring `needClass`/`needTier` for drops. */
export function toolHarvests(reg: Registries, state: number, tool: HeldTool | null): boolean {
  const name = reg.states.blockName[state];
  const def = name ? reg.blocks.byName.get(name) : undefined;
  if (!def) return true;
  const needTier = def.toolTier;
  if (!needTier) return true; // no tier gate (dirt, wood, plants)
  if (!tool?.toolClass || tool.toolClass !== def.toolClass) return false;
  const have = TIER_RANK.get(tool.toolTier ?? '') ?? -1;
  const need = TIER_RANK.get(needTier) ?? 0;
  return have >= need;
}

function tableDrops(reg: Registries, table: LootTable): ItemStack[] {
  const out: ItemStack[] = [];
  for (const pool of table.pools) {
    for (const entry of pool.entries) {
      if (entry.type !== 'item' || !entry.name) continue;
      const id = reg.items.idOf.get(entry.name);
      if (id === undefined) continue;
      let count = 1;
      for (const fn of entry.functions ?? []) {
        if (fn.function === 'set_count' || fn.set_count !== undefined) {
          const c = (fn.count ?? fn.set_count) as { min?: number } | number | undefined;
          if (typeof c === 'number') count = c;
          else if (c && typeof c.min === 'number') count = c.min;
        }
      }
      out.push(makeStack(id, count));
    }
  }
  return out;
}

/**
 * Items a block state drops when broken with `tool`. Returns [] when the tool
 * tier is too low to harvest (PLR-2). `dropSelf` yields the block's own item.
 */
export function blockDrops(reg: Registries, state: number, tool: HeldTool | null = null, opts: { fortuneRoll?: number } = {}): ItemStack[] {
  const name = reg.states.blockName[state];
  const def = name ? reg.blocks.byName.get(name) : undefined;
  if (!def || def.material === 'air') return [];
  if (!toolHarvests(reg, state, tool)) return [];

  // Silk Touch: a block that normally yields something else (ore/glass/leaves)
  // drops its own block item instead. Ignores Fortune on that break.
  if (hasSilkTouch(tool?.enchants) && harvestsSelf(def.name, def.lootTable)) {
    const id = reg.items.idOf.get(def.name);
    if (id !== undefined) return [makeStack(id, 1)];
  }

  if (def.lootTable) {
    const table = reg.loot.get(def.lootTable);
    if (!table) return [];
    const drops = tableDrops(reg, table);
    // Fortune multiplies ore drops (Java `ore_drops`), never silk-touched ones.
    const fortune = enchantLevelOf(tool?.enchants, 'fortune');
    if (fortune > 0 && opts.fortuneRoll !== undefined && harvestsSelf(def.name, def.lootTable)) {
      const mult = fortuneMultiplier(fortune, opts.fortuneRoll);
      return mult > 1 ? drops.map((d) => ({ ...d, count: d.count * mult })) : drops;
    }
    return drops;
  }
  if (def.dropSelf) {
    const id = reg.items.idOf.get(def.name);
    if (id !== undefined) return [makeStack(id, 1)];
  }
  return [];
}
